import React, { useState, useRef, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, X, Send, Loader2, User, ExternalLink, Headphones } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { supabase } from '@/integrations/supabase/client';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { trackBusinessEvent } from '@/lib/track-event';
import { subscribeNewsletter } from "@/lib/leads";

interface Message {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: Date;
}

interface SupportAgent {
  name: string;
  avatar: string;
  greeting: string;
}

interface DimitrisChatProps {
  context?: 'public' | 'account';
  userId?: string;
}

// Always Dimitris - no random agents
const getDimitrisAgent = (lang: 'el' | 'en'): SupportAgent => ({
  name: 'Dimitris',
  avatar: '👨‍💼',
  greeting: '' // No greeting - Dimitris only speaks when spoken to
});

// Get localized UI texts
const getUiTexts = (lang: 'el' | 'en') => ({
  supportTeam: 'Dimitris',
  hereToHelp: lang === 'el' ? 'EyeCaptain Support' : 'EyeCaptain Support',
  online: 'Online',
  connecting: lang === 'el' ? 'Ο Dimitris γράφει...' : 'Dimitris is typing...',
  placeholder: lang === 'el' ? 'Πληκτρολόγησε το μήνυμά σου...' : 'Type your message...',
  welcomeBack: lang === 'el' ? 'Γεια σου ξανά!' : 'Welcome back!',
  howCanIHelp: lang === 'el' ? 'Πώς μπορώ να σε βοηθήσω;' : 'How can I help you?',
  helpTopics: '',
  errorMessage: lang === 'el'
    ? 'Συγγνώμη, κάτι δεν πήγε καλά. Δοκίμασε ξανά ή επικοινώνησε μαζί μας μέσω της [φόρμας επικοινωνίας](/contact).'
    : 'Sorry, something went wrong. Try again or contact us through the [contact form](/contact).'
});

const DimitrisChat: React.FC<DimitrisChatProps> = ({ context = 'public', userId }) => {
  const { language } = useLanguage();
  const location = useLocation();
  const uiTexts = getUiTexts(language);
  
  // Hide chat on embed routes
  const isEmbedRoute = location.pathname.startsWith('/embed/');
  
  const [isOpen, setIsOpen] = useState(false);
  const handleToggleChat = (open: boolean) => {
    setIsOpen(open);
    if (open) {
      trackBusinessEvent("chatbot_opened", { page: location.pathname, trigger: "manual" });
    }
  };
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [sessionId] = useState(() => crypto.randomUUID());
  const [currentAgent, setCurrentAgent] = useState<SupportAgent | null>(null);
  const [isAgentRevealed, setIsAgentRevealed] = useState(false);
  const [showConnecting, setShowConnecting] = useState(false);
  const [pendingMessage, setPendingMessage] = useState<string | null>(null);
  
  // Lead capture state
  const [showEmailCapture, setShowEmailCapture] = useState(false);
  const [emailCaptured, setEmailCaptured] = useState(false);
  const [captureEmail, setCaptureEmail] = useState('');
  const [isCapturingEmail, setIsCapturingEmail] = useState(false);
  
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  // Check if should show email capture (after 3 user messages)
  const userMessageCount = messages.filter(m => m.role === 'user').length;
  const shouldTriggerCapture = userMessageCount >= 3 && !emailCaptured && !showEmailCapture;
  
  // Lead capture texts
  const captureTexts = {
    el: {
      title: 'Θέλετε να λάβετε τη συνομιλία στο email σας;',
      subtitle: 'Θα σας στείλουμε σύνοψη + bonus CRO tips!',
      placeholder: 'Το email σας',
      cta: 'Ναι, στείλτε μου!',
      skip: 'Όχι, ευχαριστώ',
      success: '✓ Τέλεια! Θα λάβετε email σύντομα.'
    },
    en: {
      title: 'Want this conversation sent to your email?',
      subtitle: "We'll send a summary + bonus CRO tips!",
      placeholder: 'Your email',
      cta: 'Yes, send it!',
      skip: 'No, thanks',
      success: '✓ Perfect! You\'ll receive an email soon.'
    }
  };
  
  const captureText = captureTexts[language] || captureTexts.en;

  // Trigger email capture after threshold
  useEffect(() => {
    if (shouldTriggerCapture) {
      // Small delay so it doesn't interrupt the flow
      const timer = setTimeout(() => setShowEmailCapture(true), 1500);
      return () => clearTimeout(timer);
    }
  }, [shouldTriggerCapture]);

  // Handle email capture submission
  const handleEmailCapture = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!captureEmail.trim()) return;
    
    setIsCapturingEmail(true);
    
    try {
      // Subscribe to newsletter
      await subscribeNewsletter({
        email: captureEmail,
        source: "chatbot_lead_capture",
      });
      
      // Update conversation with email
      if (conversationId) {
        await supabase
          .from('ai_conversations')
          .update({ user_email: captureEmail })
          .eq('id', conversationId);
      }
      
      setEmailCaptured(true);
      setShowEmailCapture(false);
    } catch (error) {
      console.error('Email capture failed:', error);
    } finally {
      setIsCapturingEmail(false);
    }
  };

  const dismissEmailCapture = () => {
    setShowEmailCapture(false);
    setEmailCaptured(true); // Don't show again
  };

  // Session storage key for chat persistence
  const CHAT_STORAGE_KEY = 'eyecaptain_chat_session';

  // Load chat state from sessionStorage on mount
  useEffect(() => {
    const savedChat = sessionStorage.getItem(CHAT_STORAGE_KEY);
    if (savedChat) {
      try {
        const parsed = JSON.parse(savedChat);
        if (parsed.messages?.length > 0) {
          setMessages(parsed.messages.map((m: Message) => ({
            ...m,
            timestamp: new Date(m.timestamp)
          })));
          setConversationId(parsed.conversationId);
          setCurrentAgent(parsed.currentAgent);
          setIsAgentRevealed(parsed.isAgentRevealed);
        }
      } catch (e) {
        console.error('Error loading chat state:', e);
      }
    }
  }, []);

  // Save chat state to sessionStorage whenever it changes
  useEffect(() => {
    if (messages.length > 0 && isAgentRevealed) {
      sessionStorage.setItem(CHAT_STORAGE_KEY, JSON.stringify({
        messages,
        conversationId,
        currentAgent,
        isAgentRevealed
      }));
    }
  }, [messages, conversationId, currentAgent, isAgentRevealed]);

  // No longer need welcome message logic - agent greeting is shown after first message

  // Auto scroll to bottom
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, showConnecting]);

  // Focus input when opened
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const extractLinks = (content: string): string[] => {
    const linkRegex = /\/[a-z0-9-\/]+/gi;
    return content.match(linkRegex) || [];
  };

  const saveAssistantMessage = async (content: string, convId: string) => {
    const links = extractLinks(content);
    try {
      await supabase.functions.invoke('dimitris-save-message', {
        body: {
          conversationId: convId,
          content,
          role: 'assistant',
          linksSuggested: links
        }
      });
    } catch (error) {
      console.error('Error saving assistant message:', error);
    }
  };

  // Send the actual message to the API
  const sendMessageToAPI = useCallback(async (messageContent: string, agent: SupportAgent) => {
    const userMessage: Message = {
      id: crypto.randomUUID(),
      role: 'user',
      content: messageContent,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setIsLoading(true);

    // Add 1-2 second thinking delay to feel more human
    await new Promise(resolve => setTimeout(resolve, 1000 + Math.random() * 1000));

    let assistantContent = '';
    let currentConvId = conversationId;

    try {
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/dimitris-chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
        },
        body: JSON.stringify({
          message: messageContent,
          conversationId,
          sessionId,
          userId,
          context,
          agentName: agent.name,
          language
        }),
      });

      // Get conversation ID from header
      const newConvId = response.headers.get('X-Conversation-Id');
      if (newConvId && !currentConvId) {
        currentConvId = newConvId;
        setConversationId(newConvId);
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to get response');
      }

      if (!response.body) {
        throw new Error('No response body');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let textBuffer = '';

      // Create assistant message placeholder
      const assistantMessageId = crypto.randomUUID();
      setMessages(prev => [...prev, {
        id: assistantMessageId,
        role: 'assistant',
        content: '',
        timestamp: new Date()
      }]);

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        textBuffer += decoder.decode(value, { stream: true });

        let newlineIndex: number;
        while ((newlineIndex = textBuffer.indexOf('\n')) !== -1) {
          let line = textBuffer.slice(0, newlineIndex);
          textBuffer = textBuffer.slice(newlineIndex + 1);

          if (line.endsWith('\r')) line = line.slice(0, -1);
          if (line.startsWith(':') || line.trim() === '') continue;
          if (!line.startsWith('data: ')) continue;

          const jsonStr = line.slice(6).trim();
          if (jsonStr === '[DONE]') break;

          try {
            const parsed = JSON.parse(jsonStr);
            const content = parsed.choices?.[0]?.delta?.content;
            if (content) {
              assistantContent += content;
              setMessages(prev => prev.map(m =>
                m.id === assistantMessageId
                  ? { ...m, content: assistantContent }
                  : m
              ));
            }
          } catch {
            // Incomplete JSON, wait for more data
            textBuffer = line + '\n' + textBuffer;
            break;
          }
        }
      }

      // Save assistant message to database
      if (currentConvId && assistantContent) {
        await saveAssistantMessage(assistantContent, currentConvId);
      }

    } catch (error) {
      console.error('Error sending message:', error);
      setMessages(prev => [...prev, {
        id: crypto.randomUUID(),
        role: 'assistant',
        content: uiTexts.errorMessage,
        timestamp: new Date()
      }]);
    } finally {
      setIsLoading(false);
    }
  }, [conversationId, sessionId, userId, context, language, uiTexts.errorMessage]);

  const revealAgent = useCallback((savedMessage: string | null) => {
    const agent = getDimitrisAgent(language);
    setCurrentAgent(agent);
    setShowConnecting(false);
    setIsAgentRevealed(true);
    
    // No greeting message - Dimitris only speaks when spoken to
    // Just set empty messages and send the pending message directly

    // If there was a pending message, send it after a small delay (1-2s thinking time)
    if (savedMessage) {
      const thinkingDelay = 1000 + Math.random() * 1000; // 1-2 seconds
      setTimeout(() => {
        sendMessageToAPI(savedMessage, agent);
      }, thinkingDelay);
    }
  }, [language, sendMessageToAPI]);

  const handleFirstMessage = useCallback((messageToSend: string) => {
    if (!isAgentRevealed && !showConnecting) {
      setPendingMessage(messageToSend);
      setShowConnecting(true);
      setInputValue('');
      // Wait 2 seconds then reveal agent and send the pending message
      setTimeout(() => revealAgent(messageToSend), 2000);
    }
  }, [isAgentRevealed, showConnecting, revealAgent]);

  const sendMessage = useCallback(async () => {
    if (!inputValue.trim() || isLoading) return;

    // If first message, trigger agent reveal and queue the message
    if (!isAgentRevealed) {
      handleFirstMessage(inputValue.trim());
      return;
    }

    // Agent is revealed, send message directly
    if (currentAgent) {
      const messageToSend = inputValue.trim();
      setInputValue('');
      await sendMessageToAPI(messageToSend, currentAgent);
    }
  }, [inputValue, isLoading, isAgentRevealed, handleFirstMessage, currentAgent, sendMessageToAPI]);

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (!isAgentRevealed && !showConnecting && inputValue.trim()) {
        handleFirstMessage(inputValue.trim());
      } else if (isAgentRevealed) {
        sendMessage();
      }
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };

  const handleLinkClick = (path: string) => {
    navigate(path);
    setIsOpen(false);
  };

  const renderMessageContent = (content: string) => {
    // Parse links in content
    const parts = content.split(/(\[[^\]]+\]\([^)]+\)|\/[a-z0-9-\/]+)/gi);
    
    return parts.map((part, index) => {
      // Markdown style links [text](url)
      const mdLinkMatch = part.match(/\[([^\]]+)\]\(([^)]+)\)/);
      if (mdLinkMatch) {
        return (
          <button
            key={index}
            onClick={() => handleLinkClick(mdLinkMatch[2])}
            className="text-primary hover:underline inline-flex items-center gap-1"
          >
            {mdLinkMatch[1]}
            <ExternalLink className="h-3 w-3" />
          </button>
        );
      }
      
      // Plain path links /path/to/page
      if (part.startsWith('/') && part.length > 1) {
        return (
          <button
            key={index}
            onClick={() => handleLinkClick(part)}
            className="text-primary hover:underline inline-flex items-center gap-1"
          >
            {part}
            <ExternalLink className="h-3 w-3" />
          </button>
        );
      }
      
      return <span key={index}>{part}</span>;
    });
  };

  const getHeaderContent = () => {
    if (!isAgentRevealed) {
      return {
        icon: <Headphones className="h-5 w-5" />,
        name: uiTexts.supportTeam,
        subtitle: uiTexts.hereToHelp
      };
    }
    return {
      icon: <span className="text-lg">{currentAgent?.avatar}</span>,
      name: currentAgent?.name || 'Support',
      subtitle: uiTexts.supportTeam
    };
  };

  const header = getHeaderContent();

  // Don't render anything on embed routes. This sits after every hook so
  // the hook order never changes between routes (rules-of-hooks).
  if (isEmbedRoute) return null;

  return (
    <>
      {/* Chat Toggle Button */}
        <motion.button
        initial={{ scale: 0 }}
        animate={{ scale: isOpen ? 0 : 1 }}
        className="chat-fab fixed bottom-4 right-4 z-50 flex h-12 w-12 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-[0_14px_36px_-10px_hsl(var(--primary)/0.65)] ring-1 ring-foreground/10 transition-[bottom,box-shadow,transform,opacity] duration-300 hover:scale-105 hover:shadow-[0_18px_44px_-10px_hsl(var(--primary)/0.8)] md:bottom-6 md:right-6 md:h-14 md:w-14"
        onClick={() => handleToggleChat(!isOpen)}
        aria-label={isOpen ? "Close chat" : "Open chat with AI assistant"}
        aria-expanded={isOpen}
      >
        <AnimatePresence mode="wait">
          {isOpen ? (
            <motion.div
              key="close"
              initial={{ rotate: -90, opacity: 0 }}
              animate={{ rotate: 0, opacity: 1 }}
              exit={{ rotate: 90, opacity: 0 }}
            >
              <X className="h-6 w-6" />
            </motion.div>
          ) : (
            <motion.div
              key="open"
              initial={{ rotate: 90, opacity: 0 }}
              animate={{ rotate: 0, opacity: 1 }}
              exit={{ rotate: -90, opacity: 0 }}
            >
              <MessageCircle className="h-[22px] w-[22px]" strokeWidth={2.2} />
            </motion.div>
          )}
        </AnimatePresence>
      </motion.button>

      {/* Chat Window */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            className="fixed bottom-0 left-0 right-0 z-50 flex h-[72vh] flex-col overflow-hidden rounded-t-[22px] border-t border-[hsl(var(--ec-line-strong))] bg-card text-foreground shadow-[0_30px_80px_-20px_rgba(0,0,0,0.9)] md:inset-auto md:bottom-24 md:left-auto md:right-6 md:h-[560px] md:w-[390px] md:rounded-[22px] md:border"
          >
            {/* Header */}
            <div className="relative flex items-center gap-3 border-b border-border bg-[linear-gradient(160deg,hsl(var(--primary)/0.16),transparent_70%)] px-4 py-4 safe-area-top">
              <motion.div 
                key={header.name}
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                className="grid h-10 w-10 shrink-0 place-items-center rounded-[12px] border border-[hsl(var(--ec-mint)/0.3)] bg-primary/10 text-[hsl(var(--ec-mint))]"
              >
                {header.icon}
              </motion.div>
              <div className="min-w-0 flex-1">
                <motion.h3 
                  key={header.name}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="truncate text-[15px] font-semibold"
                >
                  {header.name}
                </motion.h3>
                <p className="flex items-center gap-1.5 text-[12px] text-[hsl(var(--ec-muted))]">
                  <span className="h-1.5 w-1.5 rounded-full bg-[hsl(var(--ec-mint))] shadow-[0_0_8px_hsl(var(--ec-mint))]" />
                  {header.subtitle} · Online
                </p>
              </div>
              <button
                onClick={() => handleToggleChat(false)}
                className="ml-auto grid h-9 w-9 shrink-0 place-items-center rounded-[10px] text-[hsl(var(--ec-muted))] transition-colors hover:bg-foreground/[0.05] hover:text-foreground"
                aria-label="Close chat"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {/* Messages */}
            <ScrollArea className="flex-1 px-4" ref={scrollRef}>
              <div className="space-y-4 py-4">
                {messages.length === 0 && !showConnecting && (
                  <div className="flex flex-col items-center px-4 pt-10 text-center">
                    <span className="grid h-12 w-12 place-items-center rounded-[14px] border border-[hsl(var(--ec-mint)/0.3)] bg-primary/10 text-[hsl(var(--ec-mint))]">
                      <MessageCircle className="h-5 w-5" />
                    </span>
                    <p className="mt-4 text-[15px] font-semibold">
                      {language === 'el' ? 'Ρωτήστε μας οτιδήποτε' : 'Ask us anything'}
                    </p>
                    <p className="mt-1.5 max-w-[260px] text-[13px] leading-relaxed text-[hsl(var(--ec-muted))]">
                      {language === 'el'
                        ? 'Για το EyeCaptain, τα πακέτα ή τη σελίδα σας. Απαντάμε συνήθως μέσα σε λίγα λεπτά.'
                        : 'About EyeCaptain, the plans or your own page. We usually reply within minutes.'}
                    </p>
                  </div>
                )}
                {/* Show connecting message if waiting for agent */}
                {showConnecting && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="flex justify-center"
                  >
                    <div className="bg-muted/50 rounded-lg px-4 py-3 text-center">
                      <Loader2 className="h-5 w-5 animate-spin mx-auto mb-2 text-primary" />
                      <p className="text-sm text-muted-foreground">
                        {uiTexts.connecting}
                      </p>
                    </div>
                  </motion.div>
                )}

                {messages.map((message) => (
                  <motion.div
                    key={message.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div className={`flex gap-2 max-w-[85%] ${message.role === 'user' ? 'flex-row-reverse' : ''}`}>
                      <div className={`grid h-7 w-7 shrink-0 place-items-center rounded-[9px] ${
                        message.role === 'user' 
                          ? 'bg-primary/15 text-[hsl(var(--ec-mint))]' 
                          : 'border border-border bg-foreground/[0.03]'
                      }`}>
                        {message.role === 'user' ? (
                          <User className="h-4 w-4" />
                        ) : (
                          <span className="text-sm">{currentAgent?.avatar || '👤'}</span>
                        )}
                      </div>
                      <div className={`rounded-[16px] px-3.5 py-2.5 ${
                        message.role === 'user'
                          ? 'rounded-tr-[6px] bg-primary text-primary-foreground'
                          : 'rounded-tl-[6px] border border-border bg-foreground/[0.03] text-foreground'
                      }`}>
                        <p className="whitespace-pre-wrap text-[14px] leading-relaxed">
                          {renderMessageContent(message.content)}
                        </p>
                      </div>
                    </div>
                  </motion.div>
                ))}
                
                {isLoading && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="flex justify-start"
                  >
                    <div className="flex gap-2 items-center">
                      <div className="grid h-7 w-7 place-items-center rounded-[9px] border border-border bg-foreground/[0.03]">
                        <span className="text-sm">{currentAgent?.avatar || '👤'}</span>
                      </div>
                      <div className="rounded-[16px] rounded-tl-[6px] border border-border bg-foreground/[0.03] px-4 py-3">
                        <div className="flex items-center gap-1">
                          <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-[hsl(var(--ec-mint))]" style={{ animationDelay: '0ms' }} />
                          <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-[hsl(var(--ec-mint))]" style={{ animationDelay: '150ms' }} />
                          <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-[hsl(var(--ec-mint))]" style={{ animationDelay: '300ms' }} />
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}

                {/* Email Capture Inline Form */}
                {showEmailCapture && !emailCaptured && (
                  <motion.div
                    initial={{ opacity: 0, y: 10, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    className="rounded-[16px] border border-[hsl(var(--ec-mint)/0.3)] bg-[linear-gradient(160deg,hsl(var(--primary)/0.12),transparent_70%)] p-4"
                  >
                    <p className="font-medium text-sm mb-1">{captureText.title}</p>
                    <p className="text-xs text-muted-foreground mb-3">{captureText.subtitle}</p>
                    <form onSubmit={handleEmailCapture} className="space-y-2">
                      <Input
                        type="email"
                        placeholder={captureText.placeholder}
                        value={captureEmail}
                        onChange={(e) => setCaptureEmail(e.target.value)}
                        className="h-9 text-sm"
                        required
                        disabled={isCapturingEmail}
                      />
                      <div className="flex gap-2">
                        <Button 
                          type="submit" 
                          size="sm" 
                          className="flex-1"
                          disabled={isCapturingEmail}
                        >
                          {isCapturingEmail ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : (
                            captureText.cta
                          )}
                        </Button>
                        <Button 
                          type="button" 
                          variant="ghost" 
                          size="sm"
                          onClick={dismissEmailCapture}
                        >
                          {captureText.skip}
                        </Button>
                      </div>
                    </form>
                  </motion.div>
                )}
                
                {/* Email captured success message */}
                {emailCaptured && captureEmail && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="rounded-[12px] border border-[hsl(var(--ec-mint)/0.3)] bg-primary/10 p-3 text-center text-sm text-[hsl(var(--ec-mint))]"
                  >
                    {captureText.success}
                  </motion.div>
                )}
              </div>
            </ScrollArea>

            {/* Input */}
            <div className="border-t border-border px-3 pb-4 pt-3 safe-area-bottom">
              <div className="flex items-center gap-1 rounded-[14px] border border-[hsl(var(--ec-line-strong))] bg-background/60 p-1 focus-within:border-[hsl(var(--ec-mint)/0.5)]">
                <Input
                  ref={inputRef}
                  value={inputValue}
                  onChange={handleInputChange}
                  onKeyPress={handleKeyPress}
                  placeholder={uiTexts.placeholder}
                  disabled={isLoading || showConnecting}
                  className="h-10 flex-1 border-0 bg-transparent px-3 text-[14px] shadow-none focus-visible:ring-0 focus-visible:ring-offset-0"
                />
                <Button
                  onClick={() => {
                    if (!isAgentRevealed && !showConnecting && inputValue.trim()) {
                      handleFirstMessage(inputValue.trim());
                    } else {
                      sendMessage();
                    }
                  }}
                  disabled={!inputValue.trim() || isLoading || showConnecting}
                  size="icon"
                  aria-label="Send message"
                  className="ec-cta h-10 w-10 shrink-0 rounded-[11px]"
                >
                  {isLoading || showConnecting ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Send className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default DimitrisChat;